import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { jwtDecode } from "jwt-decode";
import { auth } from "../auth";
import { logError } from "../helpers/logError";

// Single source of truth for the active org. Reads JWT claims and tracks token
// rotations via Auth0's localStorage cache, BroadcastChannel, and auth.onTokenChanged.

const SESSION_BROADCAST_CHANNEL = "liveswitch-contact-session";
const SESSION_BROADCAST_TYPE_TOKEN_CHANGED = "token-changed";
const AUTH0_STORAGE_KEY_PREFIX = "@@auth0spajs@@";

type TokenClaims = {
	organization_id?: string;
	organization_id_client?: string;
	organization_name?: string;
	user_id?: string;
	account_id?: string;
	role_id?: string;
	role_name?: string;
	email?: string;
	name?: string;
	exp?: number;
	failed_reason?: string;
	organizations?: Array<{ id: string; name: string }>;
};

// Defense-in-depth: jwtDecode returns whatever the JWT payload happens to
// contain, with no type checking. Scribe and the SPA both read these fields,
// so a server-side regression that ships `{ organization_id: 42 }` would
// silently coerce a number into a path param. Reject malformed claims at the
// source rather than letting them propagate.
function isValidTokenClaims(claims: unknown): claims is TokenClaims {
	if (typeof claims !== "object" || claims === null) {
		return false;
	}
	const c = claims as Record<string, unknown>;
	// Treat undefined, null, and empty string identically as "claim absent."
	// JWT payloads are JSON, where null and "" are both legitimate "field is
	// empty" sentinels — rejecting either on a non-critical claim like `name`
	// or `organization_name` (e.g., a newly created org with no name yet)
	// would throw away an otherwise-valid session. Critical claims that need
	// a non-empty value (organization_id) are gated by the truthy hasOrg
	// check downstream in applyToken/initialize, which still flips
	// sessionLost when the org id is missing.
	const optionalString = (key: string): boolean => {
		const v = c[key];
		if (v === undefined || v === null || v === "") return true;
		return typeof v === "string";
	};
	return (
		optionalString("organization_id") &&
		optionalString("organization_id_client") &&
		optionalString("organization_name") &&
		optionalString("user_id") &&
		optionalString("account_id") &&
		optionalString("role_id") &&
		optionalString("role_name") &&
		optionalString("email") &&
		optionalString("name") &&
		optionalString("failed_reason") &&
		(c.exp === undefined || c.exp === null || typeof c.exp === "number")
	);
}

function decodeToken(token: string | null | undefined): TokenClaims | null {
	if (!token) {
		return null;
	}
	if (typeof token !== "string") {
		return null;
	}
	if (token.split(".").length !== 3) {
		// Non-empty value that isn't JWT-shaped: in tests this is the "mock-token"
		// sentinel and we don't want noise. In production this means the SDK
		// handed us garbage — log so Datadog sees it.
		if (token !== "mock-token") {
			logError("authSession.decodeToken: non-JWT-shaped token", new Error("non-JWT token"));
		}
		return null;
	}
	let decoded: unknown;
	try {
		decoded = jwtDecode(token);
	} catch (err) {
		logError("authSession: failed to decode token", err);
		return null;
	}
	if (!isValidTokenClaims(decoded)) {
		logError("authSession.decodeToken: claims failed shape validation", new Error("invalid claim types"), {
			keys: typeof decoded === "object" && decoded !== null ? Object.keys(decoded as Record<string, unknown>) : []
		});
		return null;
	}
	return decoded;
}

export const useAuthSessionStore = defineStore("authSession", () => {
	const claims = ref<TokenClaims | null>(null);
	const identityGeneration = ref(0);
	// True after `initialize()` has run and decoded a token whose claims
	// either have no `organization_id` (server-side action set `failed_reason`)
	// OR failed structural/type validation. Surfacing this to the UI lets us
	// show a banner instead of leaving the user silently org-less.
	const sessionLost = ref(false);
	let broadcastChannel: BroadcastChannel | null = null;
	let storageHandler: ((event: StorageEvent) => void) | null = null;
	let unsubscribeAuthListener: (() => void) | null = null;
	let unsubscribeAuthError: (() => void) | null = null;
	let initializing = false;
	let initialized = false;
	let pendingStorageRefresh = false;
	// LSP-16971: set when a storage event arrives WHILE a refresh is in flight — the drain loop
	// then does one more pass instead of dropping that cross-tab token rotation (Codex).
	let storageRefreshQueued = false;
	let lastValidatedRawToken: string | null = null;
	// LSP-16971: sticky terminal-auth flag. Set when auth emits a terminal error; kept true across
	// applyToken(null) / org-less recomputes so a refresh() that sees !isAuthenticated can't clobber
	// the session-lost banner. Cleared only on genuine recovery (a valid org token) or dispose (Bugbot).
	let terminalAuthError = false;

	// Normalize undefined/null/empty-string to null at the public surface so
	// consumers don't have to triple-check absent representations. Use `||`
	// (not `??`) so empty string falls into the absent bucket. Empty-string
	// claim values can come from the post-login Action when the upstream
	// has no value yet (newly created org with no name, etc.).
	const organizationId = computed(() => claims.value?.organization_id || null);
	const organizationName = computed(() => claims.value?.organization_name || "");
	const userId = computed(() => claims.value?.user_id || null);
	const accountId = computed(() => claims.value?.account_id || null);
	const roleName = computed(() => claims.value?.role_name || null);
	const email = computed(() => claims.value?.email || null);
	const failedReason = computed(() => claims.value?.failed_reason || null);
	const tokenExpiresAt = computed(() => (claims.value?.exp ? claims.value.exp * 1000 : null));

	function applyToken(token: string | null | undefined): boolean {
		const rawToken = typeof token === "string" ? token : null;
		const next = decodeToken(token);
		const previousId = claims.value?.organization_id;
		if (
			(claims.value?.organization_id ?? null) !== (next?.organization_id ?? null) ||
			(claims.value?.user_id ?? null) !== (next?.user_id ?? null)
		)
			identityGeneration.value += 1;
		claims.value = next;

		// Session-lost detection: only flip the flag AFTER initialization completes
		// so the boot-time "no token yet" state isn't misread as a lost session.
		// We're lost if we *had* a non-empty token but produced no usable org id,
		// OR if the server-side post-login action set a failed_reason.
		if (initialized) {
			const hadToken = rawToken !== null && rawToken !== "" && rawToken !== "mock-token";
			// Truthy check: rejects undefined, null, AND empty string. The
			// !== undefined && !== "" pair we used previously let `null`
			// through — and isValidTokenClaims now intentionally accepts
			// `null` for non-critical fields, so the validator no longer
			// catches it for us.
			const hasOrg = !!next?.organization_id;
			const hasFailureReason = !!next?.failed_reason;
			// LSP-16971: a valid token with an org means the session recovered — drop the sticky
			// terminal flag. Otherwise keep it, so applyToken(null) from a refresh() that sees
			// !isAuthenticated can't clear a terminal-set session-lost banner (Bugbot).
			if (hasOrg) {
				terminalAuthError = false;
			}
			sessionLost.value = terminalAuthError || (hadToken && !hasOrg) || hasFailureReason;
		}
		lastValidatedRawToken = rawToken;
		return previousId !== next?.organization_id;
	}

	// LSP-19862: arm the session-lost banner from outside the auth-event path. `getMe` throws
	// UnauthenticatedError from two places that never reach `_emitAuthError` — the
	// `isAuthenticated()` pre-check (api/index.ts, no token fetch at all) and a `getAccessToken()`
	// that resolves null without raising — so a caller that swallows that error would otherwise
	// leave the shell authenticated with no account and no recovery UI. Sets the same sticky
	// terminal flag the auth listener sets, so a later `applyToken(null)` recompute cannot clobber
	// the banner; a valid org token still clears it. Deliberately does not log at error level —
	// this is an expected expired-session outcome, not a fault (see App.loadBootAccountForStartup).
	function markSessionLost(): void {
		if (!sessionLost.value) identityGeneration.value += 1;
		terminalAuthError = true;
		sessionLost.value = true;
	}

	async function refresh(): Promise<void> {
		try {
			const authInstance = auth as unknown as {
				getAccessToken: (options?: { redirectOnAuthFailure?: boolean }) => Promise<string | null>;
				isAuthenticated?: () => Promise<boolean>;
			};
			const isAuthenticated = typeof authInstance.isAuthenticated === "function" ? await authInstance.isAuthenticated() : true;
			if (!isAuthenticated) {
				applyToken(null);
				return;
			}

			const token = await authInstance.getAccessToken({ redirectOnAuthFailure: false });
			if (token) {
				applyToken(token);
			} else if (!initialized) {
				applyToken(null);
			}
		} catch (err) {
			logError("authSession.refresh: failed to read token", err);
			if (!initialized) {
				claims.value = null;
			}
		}
	}

	function broadcast(): void {
		if (!broadcastChannel) {
			return;
		}
		try {
			broadcastChannel.postMessage({ type: SESSION_BROADCAST_TYPE_TOKEN_CHANGED });
		} catch (err) {
			logError("authSession.broadcast: postMessage failed", err);
		}
	}

	function notifyScribe(orgId: string | null): void {
		const authInstance = auth as unknown as { notifyScribeOrgChanged?: (id: string | null) => void };
		if (typeof authInstance.notifyScribeOrgChanged === "function") {
			authInstance.notifyScribeOrgChanged(orgId);
		}
	}

	async function initialize(): Promise<void> {
		if (initialized || initializing) {
			return;
		}
		initializing = true;

		// LSP-16971: register the terminal-auth listener BEFORE refresh() — refresh() calls
		// getAccessToken({ redirectOnAuthFailure: false }), which can emit a terminal event at
		// boot; registering it afterward would drop that first emission and delay the
		// session-lost banner until a later failure (Bugbot).
		const authErrorInstance = auth as unknown as {
			onAuthError?: (cb: (e: { terminal: boolean }) => void) => () => void;
		};
		if (typeof authErrorInstance.onAuthError === "function") {
			unsubscribeAuthError = authErrorInstance.onAuthError((event) => {
				if (event?.terminal) {
					logError("authSession: terminal auth error — surfacing session-lost banner", new Error("terminal auth"));
					terminalAuthError = true;
					sessionLost.value = true;
				}
			});
		}

		await refresh();
		// Mark as initialized BEFORE wiring listeners so any subsequent applyToken
		// (from a storage event arriving in the same tick, etc.) can correctly
		// flip sessionLost.
		initialized = true;
		initializing = false;
		// Re-evaluate sessionLost now that we've moved past boot.
		const hadInitialToken = lastValidatedRawToken !== null && lastValidatedRawToken !== "" && lastValidatedRawToken !== "mock-token";
		// Truthy check rejects undefined, null, and empty string in one step.
		const hasOrg = !!claims.value?.organization_id;
		const hasFailureReason = !!claims.value?.failed_reason;
		// Include the sticky terminal flag so a terminal error caught during refresh() above is not
		// clobbered by the boot re-eval (Bugbot).
		sessionLost.value = terminalAuthError || (hadInitialToken && !hasOrg) || hasFailureReason;

		if (typeof BroadcastChannel !== "undefined") {
			try {
				broadcastChannel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
				broadcastChannel.addEventListener("message", (event) => {
					if (event.data?.type === SESSION_BROADCAST_TYPE_TOKEN_CHANGED) {
						void refresh();
					}
				});
			} catch (err) {
				logError("authSession.initialize: BroadcastChannel setup failed", err);
			}
		}

		// Auth0 SDK writes 2-3 keys per rotation (token blob, expiry, etc.). Coalesce them so we
		// refresh once per rotation, but DRAIN in a loop: a storage event that arrives mid-refresh
		// sets storageRefreshQueued so we run one more pass instead of dropping it. Dropping it could
		// leave us applying stale state when another tab rotates/switches tokens during our in-flight
		// refresh (Codex).
		const drainStorageRefresh = async (): Promise<void> => {
			try {
				do {
					storageRefreshQueued = false;
					await refresh();
				} while (storageRefreshQueued);
			} finally {
				pendingStorageRefresh = false;
			}
		};
		storageHandler = (event: StorageEvent) => {
			if (!event.key?.startsWith(AUTH0_STORAGE_KEY_PREFIX)) {
				return;
			}
			if (pendingStorageRefresh) {
				// A refresh is already running — record that another rotation happened so the drain
				// loop picks it up rather than dropping it.
				storageRefreshQueued = true;
				return;
			}
			pendingStorageRefresh = true;
			queueMicrotask(() => {
				void drainStorageRefresh();
			});
		};
		window.addEventListener("storage", storageHandler);

		const authInstance = auth as unknown as {
			onTokenChanged?: (cb: (token: string | null) => void) => () => void;
		};
		if (typeof authInstance.onTokenChanged === "function") {
			unsubscribeAuthListener = authInstance.onTokenChanged((token) => {
				const orgChanged = applyToken(token);
				if (orgChanged) {
					broadcast();
					notifyScribe(claims.value?.organization_id ?? null);
				}
			});
		}
	}

	function dispose(): void {
		if (storageHandler) {
			window.removeEventListener("storage", storageHandler);
			storageHandler = null;
		}
		if (broadcastChannel) {
			try {
				broadcastChannel.close();
			} catch (err) {
				logError("authSession.dispose: BroadcastChannel.close failed", err);
			}
			broadcastChannel = null;
		}
		if (unsubscribeAuthListener) {
			unsubscribeAuthListener();
			unsubscribeAuthListener = null;
		}
		if (unsubscribeAuthError) {
			unsubscribeAuthError();
			unsubscribeAuthError = null;
		}
		// Clear auth.js's dedup cache so a re-initialize after dispose
		// (HMR / tests) doesn't silently drop the first emit when the
		// SDK happens to return the same token string.
		const authInstance = auth as unknown as { resetTokenCache?: () => void };
		if (typeof authInstance.resetTokenCache === "function") {
			authInstance.resetTokenCache();
		}
		initialized = false;
		initializing = false;
		pendingStorageRefresh = false;
		storageRefreshQueued = false;
		lastValidatedRawToken = null;
		terminalAuthError = false;
		sessionLost.value = false;
		claims.value = null;
	}

	return {
		identityGeneration,
		organizationId,
		organizationName,
		userId,
		accountId,
		roleName,
		email,
		failedReason,
		tokenExpiresAt,
		sessionLost,
		markSessionLost,
		initialize,
		refresh,
		dispose
	};
});
